/**
 * The reference's modal and drawer, as React components — `openModal` in `m01-utils.js`.
 *
 * Same markup, same behaviour: a `.modal-back` overlay hosting a `.modal` dialog, `is-drawer` +
 * `drawer` for the side panel, `wide` for the 680px variant. And the same accessibility, which is
 * the part that is easy to lose in a port and impossible to add back later:
 *
 *   - focus moves into the dialog, skipping the close button, and returns to whatever opened it;
 *   - Tab and Shift+Tab cycle inside the dialog rather than escaping to the page behind;
 *   - Escape closes, and so does a click on the backdrop — unless the dialog is `sticky`.
 *
 * A dialog is rendered into `#modalHost`, as the reference does, so it is never clipped by a
 * screen's own stacking context.
 */
import { type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  label: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  drawer?: boolean;
  /** A backdrop click does not close a sticky dialog, as upstream. */
  sticky?: boolean;
}

export function Modal({ label, onClose, children, wide, drawer, sticky }: ModalProps): ReactElement {
  const dialog = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);

  const focusables = useCallback((): HTMLElement[] => {
    const node = dialog.current;
    if (node === null) return [];
    return [...node.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
  }, []);

  /* Remember the opener, move focus in, and give it back on the way out. */
  useEffect(() => {
    opener.current = document.activeElement;
    const timer = window.setTimeout(() => {
      const list = focusables();
      const target = list.find((el) => !el.hasAttribute('data-close')) ?? list[0] ?? dialog.current;
      target?.focus();
    }, 30);
    return () => {
      window.clearTimeout(timer);
      const previous = opener.current;
      if (previous instanceof HTMLElement && document.contains(previous)) previous.focus();
    };
  }, [focusables]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const trap = (event: React.KeyboardEvent): void => {
    if (event.key !== 'Tab') return;
    const list = focusables();
    if (list.length === 0) {
      event.preventDefault();
      return;
    }
    const first = list[0] as HTMLElement;
    const last = list[list.length - 1] as HTMLElement;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /*
   * Resolved ONCE, for the life of this dialog.
   *
   * It used to be looked up on every render, and a portal whose container CHANGES is unmounted from
   * the old one and mounted into the new — the whole dialog, its effects and everything it had
   * fetched. That happens whenever `#modalHost` is not in the document yet during the dialog's
   * first render pass and is by its second: the lookup answered `document.body`, then answered the
   * host, and the dialog was built twice. In the product the host is mounted by the shell long
   * before any dialog opens, so this is not what a member sees — but it is a remount waiting for
   * the first tree that renders the two together, and the share dialog is the one place where a
   * remount costs a fetched image and a connection in progress.
   *
   * Pinning it keeps a dialog wherever it was opened. A host that appears later does not steal a
   * dialog that is already on screen, and nothing moves.
   */
  const host = useMemo(() => document.getElementById('modalHost') ?? document.body, []);

  return createPortal(
    /* The backdrop click is a mouse convenience that the reference also offers; it is never the
       only way out, so it needs no keyboard handler of its own. Escape closes the dialog and the
       header's × is in the focus trap — both are exercised by the acceptance run. */
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismissal, duplicated by Escape and the close button
    // biome-ignore lint/a11y/useKeyWithClickEvents: same — the keyboard paths are Escape and the × button
    <div
      className={`modal-back${drawer ? ' is-drawer' : ''}`}
      id="modalBack"
      onClick={(event) => {
        if (event.target === event.currentTarget && !sticky) onClose();
      }}
    >
      <div
        className={`modal ${wide ? 'wide' : ''} ${drawer ? 'drawer' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        ref={dialog}
        onKeyDown={trap}
      >
        {children}
      </div>
    </div>,
    host,
  );
}

/** The reference's dialog header: title on the left, a `data-close` × on the right. */
export function ModalHead({ title, onClose }: { title: string; onClose: () => void }): ReactElement {
  return (
    <div className="card-head">
      <h2>{title}</h2>
      <button className="icon-btn" type="button" data-close="1" aria-label="Kapat" onClick={onClose}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
